import { json, LoaderFunction, redirect } from "@remix-run/node";
import { MetaFunction, useLoaderData } from "@remix-run/react";

import Stripe from "stripe";
import Footer from "~/Components/Footer";
import Cart from "~/Components/OrderPage/Cart";
import CheckoutButton from "~/Components/OrderPage/CheckoutButton";

import ShippingInformation from "~/Components/OrderPage/ShippingInformation";
import { ShoppingCartProvider } from "~/utils/context/useShoppingCart";
import fetchWrapper from "~/utils/fetchWrapper";
import formatOrderObject from "~/utils/formatter/formatOrderObject";
import getErrorMessage from "~/utils/getErrorMessage";
import { getOrderFromRedis, setOrderInRedis } from "~/utils/redis/order";
import setSentryError from "~/utils/setSentryError";
import { orderDataType } from "~/utils/types/LoaderData/order";
import { commitSession, getSession } from "~/session.server";
import { useEffect, useState } from "react";
import FAQ from "~/Components/FAQ";
import { getCurrencyDetailsInUSD } from "~/utils/getCurrencyDetails";
import processOrder from "~/utils/processOrder";

export const meta: MetaFunction = () => {
  return [
    { title: "Order Xaman Cards - XRPL Labs | Xaman" },
    { name: "description", content: "Xaman Cards: Your Ultimate Cold Wallet" }
  ];
};

export const loader: LoaderFunction = async ({ request }): Promise<orderDataType | any> => {
  // const ip = getClientIPAddress(request.headers)
  const ip = request.headers.get("cf-connecting-ip") || '8.8.8.8'
  let currency = 'USD'
  let currencyRate = 1;
  const country = request.headers.get("CF-IPCountry") || 'US';

  if (ip) {
    let currencyRequest: any = await fetchWrapper(`https://ipapi.co/${ip}/json?key=${process.env.IP_API_KEY}`, { method: 'GET' });
    // There can be a rate limit on ipapi.co, so reset to USD if that happens.
    if (currencyRequest && currencyRequest.code === 500) {
      currency = 'USD';
    } else {
      currency = currencyRequest.currency
    }
  }

  if (currency !== 'USD') {
    const currencyRequest = await fetch(`${process.env.XAMAN_API_ENDPOINT}rates/${currency}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': process.env.XAMAN_API_KEY || '',
        'X-API-Secret': process.env.XAMAN_API_SECRET || ''
      }
    });
    const currencyDetails = await currencyRequest.json();

    if (currencyDetails.USD) {
      currencyRate = currencyDetails.USD
    }
  }

  const currencyDetailsInUSD = await getCurrencyDetailsInUSD();

  let { searchParams } = new URL(request.url);
  const sessionID = searchParams.get('session_id');
  const orderID = searchParams.get('order_id');
  let orderIsVerified = false;
  let order: any | null = null;

  if (sessionID && orderID) {
    // Verify order payment status
    const stripe = new Stripe(process.env.STRIPE_PRIVATE_KEY || '')
    const stripeSession = await stripe.checkout.sessions.retrieve(sessionID);
    let sessionIsExpired = Math.floor(Date.now() / 1000) > stripeSession.expires_at;

    if (stripeSession.payment_status === 'paid' && stripeSession.metadata && stripeSession.metadata.orderID === orderID && !sessionIsExpired) {
      // Get order from redis
      const orderFromRedis = await getOrderFromRedis(orderID);

      if (!orderFromRedis || !orderFromRedis.currencyPrice) {
        const sentryError = setSentryError('No order found for ID', { orderID, stripeSession: stripeSession }, { level: 'error' });
        return json({
          googleAPIKey: process.env.GOOGLE_API_KEY || '',
          currency,
          currencyRate,
          status: 'error',
          errorCode: 1002,
          errorID: sentryError
        })
      }

      if (orderFromRedis.submitted) {
        const session = await getSession(request.headers.get('Cookie') || '');
        await session.set('orderID', orderID);

        return redirect('/order-complete', {
          headers: {
            "Set-Cookie": await commitSession(session),
          },
        });
      }

      // Verify order paid amount
      orderFromRedis.stripeReference = String(stripeSession.payment_intent);
      orderFromRedis.stripeTxID = sessionID;
      order = formatOrderObject(orderFromRedis);
      let orderNumber = order.orderNumber
      const orderProcessed = await processOrder(orderFromRedis, stripeSession, 'loader');
      if (orderProcessed === true) {
        const session = await getSession(request.headers.get('Cookie') || '');
        await session.set('orderID', orderID);

        return redirect('/order-complete', {
          headers: {
            "Set-Cookie": await commitSession(session),
          },
        });
      } else {
        return json({
          googleAPIKey: process.env.GOOGLE_API_KEY || '',
          currency,
          currencyRate,
          currencyDetailsInUSD,
          status: orderProcessed && orderProcessed?.status || 'error',
          //@ts-ignore
          errorCode: orderProcessed && orderProcessed?.errorCode,
          orderNumber: orderNumber
        })
      }

    }
  }

  if (orderID) {
    order = await getOrderFromRedis(orderID);
  }

  return json({
    googleAPIKey: process.env.GOOGLE_API_KEY || '',
    currency,
    currencyRate,
    currencyDetailsInUSD,
    orderIsVerified,
    order,
    country
  })
}



export default function order() {
  const { currencyRate, currency, status, currencyDetailsInUSD, errorCode, orderNumber, errorID, order, country } = useLoaderData<orderDataType>();
  const [parcelError, setParcelError] = useState<{ message: string } | null>(null);
  const [subButtonMessage, setSubButtonMessage] = useState<string>('Select two or more cards to proceed.');

  useEffect(() => {
    if (status === 'already_processing') {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 2000); // Check every 2 seconds

      return () => clearTimeout(timer);
    }
  }, [status]);

  return (
    <ShoppingCartProvider>
      {status === 'error' && errorCode ?
        <div className="w-screen min-h-[90vh]">
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60">
            <div className="w-3/4 bg-white z-[99999] flex flex-col items-center p-12">
              <h2 className="text-xl font-bold">{getErrorMessage(errorCode).title}</h2>
              <p>{getErrorMessage(errorCode).message}</p>
              <code className="mt-2">{orderNumber}</code>
              <div className="mt-8">Also provide us this error code</div>
              <code className="mt-2">{errorID}</code>
            </div>
          </div>
        </div>
        :
        <div className="w-screen bg-gray-50">
          {status === 'already_processing' ?
            <div className="w-screen min-h-[90vh]">
              <div className="flex items-center justify-center min-h-[90vh]">
                <div className="w-3/4 max-w-lg bg-white p-12 rounded-lg shadow-lg text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto mb-6"></div>
                  <h2 className="text-2xl font-bold mb-4">Processing Your Order</h2>
                  <p className="text-lg mb-4">We're finalizing your order. This usually takes just a few seconds.</p>
                  <p className="text-sm text-gray-600">Order #{orderNumber}</p>
                </div>
              </div>
            </div>
            :
            <>
              <div className="px-4 pt-16 pb-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto lg:max-w-none">
                  <h1 className="mb-4 text-3xl font-bold">Xaman Tangem Card Collection</h1>
                  <h3 className="text-lg mb-4">Each Xaman Tangem card is a sleek, tap-to-sign NFC hardware wallet—compact, secure, and designed for effortless self-custody of XRP and XAH. Learn more in our <a className="underline cursor-pointer hover:text-brand-blueHover" href="https://help.xaman.app/app/security-and-xaman/all-about-security/xumm-tangem-cards" target="_blank">help center</a>. To use Xaman Cards your phone needs to have an update version of iOS/Android and have NFC capabilities.</h3>
                  <p className="text-lg mb-8">We recommend ordering <strong>two cards</strong> — one as your primary, the other as a backup, so you’ll always have access to your funds even if one gets lost or damaged</p>
                  <form className="grid items-start lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16" id="orderForm">
                    <Cart currency={currency} currencyRate={currencyRate} currencyDetailsInUSD={currencyDetailsInUSD} setSubButtonMessage={setSubButtonMessage} order={order} />
                    <div className="order-2 col-span-2 px-4 py-6 mt-8 bg-white border border-gray-200 rounded-lg shadow-md sm:px-6">
                      <ShippingInformation parcelError={parcelError} setSubButtonMessage={setSubButtonMessage} order={order} countryAbbr={country || ''} />
                      <div className="pt-10 mt-10 border-t border-gray-200">
                        <CheckoutButton currency={currency} setParcelError={setParcelError} subButtonMessage={subButtonMessage} buttonMessage="Proceed to Checkout" />
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <FAQ />
            </>
          }
        </div>
      }
      <Footer />
    </ShoppingCartProvider >
  )
};
